import { useEffect, useState } from "react";
import { Plus, Loader2, Pencil, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import BlogPostModal from "@/components/BlogPostModal";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function AdminBlogTable() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/blog")
      .then(({ data }) => setPosts(data))
      .catch((err) => {
        toast.error("Failed to load articles", {
          description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (post) => {
    setEditing(post);
    setModalOpen(true);
  };

  const deletePost = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blog/${post.id}`);
      toast.success("Article deleted");
      load();
    } catch (err) {
      toast.error("Delete failed", {
        description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    }
  };

  return (
    <section
      data-testid="admin-blog-manager"
      className="mt-10 rounded-2xl border border-white/10 bg-[#0E0E12] overflow-hidden"
    >
      <div className="p-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-label text-[#00E5FF]">Blog Management</div>
          <h2 className="font-display text-2xl mt-1">All articles</h2>
        </div>
        <button
          type="button"
          onClick={openCreate}
          data-testid="blog-create-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[#E0007A] hover:bg-[#ff1493] text-white font-semibold text-sm px-5 py-2.5 glow-magenta transition-transform hover:scale-[1.02]"
        >
          <Plus className="h-4 w-4" />
          Create New Article
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-white/40" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-blog-table">
            <thead className="bg-black/40 text-white/50 font-mono-label">
              <tr>
                <th className="text-left px-6 py-3">Title</th>
                <th className="text-left px-6 py-3">Category</th>
                <th className="text-left px-6 py-3">Status</th>
                <th className="text-left px-6 py-3">Published Date</th>
                <th className="text-left px-6 py-3">Views</th>
                <th className="text-right px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`admin-blog-row-${p.id}`}
                  className="border-t border-white/5 hover:bg-white/[0.02]"
                >
                  <td className="px-6 py-3 max-w-[320px] truncate">{p.title}</td>
                  <td className="px-6 py-3 text-white/60">{p.category}</td>
                  <td className="px-6 py-3">
                    <span
                      data-testid={`admin-blog-status-${p.id}`}
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-mono-label ${
                        p.status === "published"
                          ? "bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/25"
                          : "bg-white/5 text-white/50 border border-white/15"
                      }`}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-white/60 font-mono text-xs">
                    {formatDate(p.created_at)}
                  </td>
                  <td className="px-6 py-3 text-white/60">
                    <span className="inline-flex items-center gap-1.5">
                      <Eye className="h-3 w-3" />
                      {p.views}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      <button
                        onClick={() => openEdit(p)}
                        data-testid={`admin-blog-edit-${p.id}`}
                        className="inline-flex items-center gap-1 text-[#00E5FF] hover:text-white transition-colors text-xs"
                      >
                        <Pencil className="h-3 w-3" />
                        Edit
                      </button>
                      <button
                        onClick={() => deletePost(p)}
                        data-testid={`admin-blog-delete-${p.id}`}
                        className="inline-flex items-center gap-1 text-red-300 hover:text-red-100 transition-colors text-xs"
                      >
                        <Trash2 className="h-3 w-3" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {posts.length === 0 && (
            <div className="text-center py-10 text-white/50" data-testid="admin-blog-empty">
              No articles yet — create your first one.
            </div>
          )}
        </div>
      )}

      {modalOpen && (
        <BlogPostModal
          post={editing}
          onClose={() => setModalOpen(false)}
          onSaved={() => load()}
        />
      )}
    </section>
  );
}
